import React, { useEffect, useRef, useState } from 'react';

import { useLayoutStore, usePortalStore } from '../../../../stores/portalStore';
import { useOrderStore } from '../../store/ordersStore';
import { UserHelper } from '../../../../services/auth/UserHelper';
import { OrdersDataSource } from '../../../../datasources/orders/OrdersDataSource';
import { OrderEnums } from '../../../../shared/constants/OrderEnums';
import { LargeTile } from '../../../../shared/ui/grid/gridsystem';
import { TitledPanel } from '../../../../shared/ui/dashboard/titledPanel/titledPanel';
import { ImageSlider } from '../../../../shared/media/slider';
import { ErrorModal } from '../../../../shared/feedback/error';
import { InfoModal } from '../../../../shared/ui/Modal/InfoModal';
import { EditOrderModal } from '../EditOrderModal';
import { OrdersHistory } from './OrdersHistory';
import { OrdersActionBar } from './OrdersActionBar';
import { CancelOrderPopup } from './CancelOrderPopup';
import { useResponsive } from '../../../../hooks/useResponsive';

const orderEnums = new OrderEnums();

const hasPatientNameFilter = (filters: any[]) => {
    const patientFilter = filters?.find((filter) => filter?.name === 'Paciente');
    return String(patientFilter?.value ?? '').trim() !== '';
};

const getOrdersFetchErrorMessage = (filters: any[]) => {
    if (hasPatientNameFilter(filters)) {
        return 'Não foi possível buscar pelo nome do paciente. Tente outro filtro.';
    }

    return 'Não foi possível carregar os pedidos. Tente novamente em alguns instantes.';
};

export const OrdersMainPanels: React.FC = () => {
    const currentUser = usePortalStore((state: any) => state.currentUser);
    const setCurrentAlertError = usePortalStore((state: any) => state.setCurrentAlertError);
    const contentWidth = useLayoutStore((state) => state.contentWidth);
    const contentHeight = useLayoutStore((state) => state.contentHeight);
    const { isDesktop } = useResponsive();
    const containerWidth = isDesktop && contentWidth ? contentWidth : '100%';
    const containerHeight = isDesktop && contentHeight ? contentHeight - 35 : '90vh';

    const fetchOrders = useOrderStore((state) => state.fetchOrders);
    const uploadCEC = useOrderStore((state) => state.uploadCEC);
    const setPagination = useOrderStore((state) => state.setPagination);
    const page = useOrderStore((state) => state.page);
    const pageSize = useOrderStore((state) => state.pageSize);

    const [orderCancel, setOrderCancel] = useState<any | null>(null);
    const [orderEdit, setOrderEdit] = useState<any | null>(null);
    const [showEditOrderModal, setShowEditOrderModal] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState<any | null>(null);
    const [selectedFilters, setSelectedFilters] = useState<any[]>([]);
    const [openActivity, setOpenActivity] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [showCECErrorModal, setShowCECErrorModal] = useState(false);
    const [showCECSuccessModal, setShowCECSuccessModal] = useState(false);

    const hiddenFileInput = useRef<HTMLInputElement | null>(null);

    // ===== Carregar pedidos sempre que filtros/paginação mudam =====
    useEffect(() => {
        (async () => {
            try {
                setIsLoading(true);
                await fetchOrders(currentUser.cnpj, selectedFilters);
            } catch (error) {
                console.error('[OrdersMainPanels] erro ao buscar pedidos', error);
                setCurrentAlertError(getOrdersFetchErrorMessage(selectedFilters));
            } finally {
                setIsLoading(false);
            }
        })();
    }, [selectedFilters, page, pageSize, currentUser.cnpj, fetchOrders, setCurrentAlertError]);

    // ===== Busca (filtros e paginação) =====
    const onSearch = async (filters: any[] | null, pagination?: any) => {
        if (filters !== null) {
            setSelectedFilters([...filters]);
            setPagination(0, 10);
        }
        if (pagination) {
            setPagination(pagination.page, pagination.pageSize);
        }
    };

    // ===== Cancelar pedido (abre popup) =====
    const onCancelOrder = async (order: any) => {
        setOrderCancel({ ...order });
        setErrorMessage('');
    };

    // ===== Editar pedido (abre modal) =====
    const onOrderEdit = async (order: any) => {
        setOrderEdit({ ...order, sapNumbers: order.sap_numbers });
        setShowEditOrderModal(true);
    };

    // ===== Upload de CEC: input de arquivo escondido =====
    const uploadCECFile = async (order: any) => {
        setSelectedOrder(order);
        hiddenFileInput.current?.click();
    };

    const handleChangeCECFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files;

        if (files && selectedOrder) {
            const allFiles: { name: string; content: string | ArrayBuffer | null }[] = [];

            const filePromises = Array.from(files).map((file) => {
                const fileName = file.name.split('.')[0];
                const isNumberRegex = /^[0-9]*$/g;

                if (fileName.length !== 8 || !isNumberRegex.test(fileName)) {
                    setShowCECErrorModal(true);
                    return;
                }

                return new Promise<void>((resolve, reject) => {
                    const reader = new FileReader();

                    reader.onload = async (ev) => {
                        const fileContent = ev.target?.result || null;
                        allFiles.push({
                            name: fileName,
                            content: fileContent,
                        });
                        resolve();
                    };

                    reader.onerror = (error) => {
                        reject(error);
                    };

                    reader.readAsDataURL(file);
                });
            });

            await Promise.all(filePromises);

            if (allFiles.length !== 0) {
                for (const file of allFiles) {
                    await uploadCEC({
                        request_number: selectedOrder.number,
                        user: UserHelper.getCurrentUser()?.username ?? '',
                        files: [file],
                    });
                }

                setShowCECSuccessModal(true);
                onSearch(selectedFilters);
            }
        }

        e.target.value = '';
    };

    // ===== Atualizar tabela após edição =====
    const refreshOrdersTable = async () => {
        try {
            setIsLoading(true);
            await fetchOrders(currentUser.cnpj, selectedFilters);
        } catch (error) {
            console.error('[OrdersMainPanels] erro ao atualizar pedidos', error);
            setCurrentAlertError(getOrdersFetchErrorMessage(selectedFilters));
        } finally {
            setIsLoading(false);
        }
    };

    // ===== Cancelamento efetivo no backend =====
    const cancelOrder = async () => {
        if (!orderCancel) return;

        try {
            setIsLoading(true);
            const ordersDataSource = new OrdersDataSource();
            const dbOrder = await ordersDataSource.getOrderById(orderCancel.id);

            if (!dbOrder || dbOrder.id == null) {
                console.error('[cancelOrder] Pedido não encontrado ou sem ID válido.', dbOrder);
                setErrorMessage('Não foi possível localizar o pedido para cancelamento.');
                return;
            }

            const newData = {
                delivery_forecast: dbOrder.delivery_forecast,
                delivery_window: dbOrder.delivery_window,
                id: dbOrder.id,
                request_number: dbOrder.request_number,
                operator_cognito_id: undefined,
                status_id: orderEnums.enums.status.UNSUCCESSFUL,
                status_complement_id: undefined,
                delivery_operations: [] as string[],
                service_events: [] as string[],
                retreat_reasons: [] as string[],
                distribution_center: dbOrder.distribution_center,
            };

            await new OrdersDataSource().update(newData);
            await fetchOrders(currentUser.cnpj);
            setOrderCancel(null);
        } catch (error) {
            console.error('[cancelOrder] erro ao cancelar pedido', error);
            setErrorMessage('Este pedido não pode ser cancelado no status atual.');
        } finally {
            setIsLoading(false);
        }
    };

    // ===== Atividade do pedido =====
    const onOpenActivity = (order: any) => {
        setSelectedOrder(order);
        setOpenActivity(true);
    };

    const handleCloseEditModal = () => {
        setShowEditOrderModal(false);
    };

    return (
        <>
            {/* Input escondido para upload de CEC */}
            <input type="file" style={{ display: 'none' }} multiple ref={hiddenFileInput} onChange={handleChangeCECFile} />

            {/* Slider de atividade */}
            {openActivity && <ImageSlider images={[]} />}

            {/* Popup de cancelamento */}
            {orderCancel && (
                <CancelOrderPopup
                    order={orderCancel}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage}
                    onClose={() => {
                        setErrorMessage('');
                        setOrderCancel(null);
                    }}
                    onConfirm={cancelOrder}
                />
            )}

            {/* Modal de edição de pedido */}
            <EditOrderModal
                order_id={orderEdit?.id}
                open={showEditOrderModal}
                handleClose={handleCloseEditModal}
                refreshOrdersTable={refreshOrdersTable}
            />

            {/* Painel principal */}
            <LargeTile
                style={{
                    maxWidth: containerWidth,
                    margin: '15px',
                    display: 'flex',
                    flexDirection: 'column',
                    height: containerHeight,
                }}
            >
                <TitledPanel
                    title="Histórico de pedidos"
                    outlet={
                        <OrdersHistory
                            currentUser={currentUser}
                            onSearch={onSearch}
                            onOrderEdit={onOrderEdit}
                            hiddenFileInput={hiddenFileInput}
                            uploadCECFile={uploadCECFile}
                            handleChangeCECFile={handleChangeCECFile}
                            onCancelOrder={onCancelOrder}
                            onOpenActivity={onOpenActivity}
                            isLoading={isLoading}
                            selectedFilters={selectedFilters}
                        />
                    }
                    action={<OrdersActionBar />}
                />
            </LargeTile>

            {/* Modais de CEC */}
            <ErrorModal
                showErrorModal={showCECErrorModal}
                setShowErrorModal={setShowCECErrorModal}
                message="O nome do arquivo deve possuir apenas números e deve ter 8 dígitos. Certifique-se que o número SAP foi adicionado ao pedido."
            />

            <InfoModal
                open={showCECSuccessModal}
                handleClose={() => setShowCECSuccessModal(false)}
                message="Upload de CEC realizado com sucesso!"
            />
        </>
    );
};
